import { Fragment, useState } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { X, AlertCircle, CheckCircle, Clock, AlertTriangle } from "lucide-react"
import creditAccountHistoryService from "../../services/creditAccountHistoryService"
import toast from "react-hot-toast"

const ChangeStatusModal = ({ isOpen, onClose, transaction, reset }) => {
  const [selectedStatus, setSelectedStatus] = useState("")
  const [rejectionReason, setRejectionReason] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleAfterOpen = () => {
    if (transaction) {
      setSelectedStatus(transaction.status)
      setRejectionReason("")
    }
  }

  const handleSubmit = async () => {
    try {
      if (!transaction || selectedStatus === transaction.status) {
        onClose()
        return
      }

      if (selectedStatus === "rejected" && !rejectionReason.trim()) {
        toast.error("Please provide a rejection reason")
        return
      }

      setIsSubmitting(true)

      const response = await creditAccountHistoryService.updateStatus({
        _id: transaction.rawData._id,
        transaction: transaction.id,
        status: selectedStatus,
        rejectionReason: selectedStatus === "rejected" ? rejectionReason.trim() : undefined,
      })

      if (response.data && response.data.statusCode === 200) {
        toast.success("Status updated successfully")
        reset()
        onClose()
      }
    } catch (error) {
      console.error("Failed to change status:", error.response?.data?.message)
      toast.error(error.response?.data?.message || "Failed to update status")
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!transaction) return null

  return (
    <Transition appear show={isOpen} as={Fragment} afterEnter={handleAfterOpen}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl border border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-xl transition-all">
                <div className="border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-lg font-medium text-neutral-900 dark:text-white">
                      Change Transaction Status
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <div className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Transaction ID</div>
                    <div className="text-sm font-medium text-neutral-900 dark:text-white">{transaction.id}</div>
                  </div>

                  <div className="mb-6">
                    <div className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Current Status</div>
                    <div className="text-sm font-medium">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          transaction.status === "completed"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : transaction.status === "pending"
                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                        }`}
                      >
                        <span
                          className={`w-2 h-2 mr-1.5 rounded-full ${
                            transaction.status === "completed"
                              ? "bg-green-500"
                              : transaction.status === "pending"
                              ? "bg-yellow-500"
                              : "bg-red-500"
                          }`}
                        ></span>
                        {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                      </span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="text-sm text-neutral-600 dark:text-neutral-400 mb-3">Select New Status</div>
                    <div className="grid grid-cols-1 gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedStatus("completed")}
                        className={`flex items-center gap-3 p-3 border rounded-xl transition-colors ${
                          selectedStatus === "completed"
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-700"
                            : "border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                        }`}
                      >
                        <CheckCircle
                          size={20}
                          className={selectedStatus === "completed" ? "text-green-500" : "text-neutral-400"}
                        />
                        <div className="text-left">
                          <div className="text-sm font-medium text-neutral-900 dark:text-white">Completed</div>
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">
                            Transaction has been successfully processed
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setSelectedStatus("pending")}
                        className={`flex items-center gap-3 p-3 border rounded-xl transition-colors ${
                          selectedStatus === "pending"
                            ? "border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700"
                            : "border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                        }`}
                      >
                        <Clock
                          size={20}
                          className={selectedStatus === "pending" ? "text-yellow-500" : "text-neutral-400"}
                        />
                        <div className="text-left">
                          <div className="text-sm font-medium text-neutral-900 dark:text-white">Pending</div>
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">
                            Transaction is being processed
                          </div>
                        </div>
                      </button>

                      <button
                        type="button"
                        onClick={() => setSelectedStatus("rejected")}
                        className={`flex items-center gap-3 p-3 border rounded-xl transition-colors ${
                          selectedStatus === "rejected"
                            ? "border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-700"
                            : "border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                        }`}
                      >
                        <AlertTriangle
                          size={20}
                          className={selectedStatus === "rejected" ? "text-red-500" : "text-neutral-400"}
                        />
                        <div className="text-left">
                          <div className="text-sm font-medium text-neutral-900 dark:text-white">Rejected</div>
                          <div className="text-xs text-neutral-500 dark:text-neutral-400">
                            Transaction could not be processed
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {selectedStatus === "rejected" && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-red-700 dark:text-red-400 mb-2">
                        Rejection Reason *
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Explain why this transaction was rejected..."
                        rows={4}
                        className="w-full border p-3 text-sm border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 rounded-xl placeholder-neutral-500 text-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-red-500"
                      />
                    </div>
                  )}

                  {transaction.status === "completed" && selectedStatus !== "completed" && (
                    <div className="mb-6 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl flex items-start gap-3">
                      <AlertCircle size={20} className="text-yellow-500 shrink-0 mt-0.5" />
                      <div className="text-sm text-yellow-800 dark:text-yellow-400">
                        Changing a completed transaction's status may affect account balance calculations and reporting.
                      </div>
                    </div>
                  )}
                </div>

                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-5 flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-xl transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isSubmitting ||
                      selectedStatus === transaction.status ||
                      (selectedStatus === "rejected" && !rejectionReason.trim())
                    }
                    className="px-4 py-2 text-sm font-[400] text-white dark:text-neutral-900 rounded-xl transition-colors bg-neutral-900 hover:bg-neutral-950 dark:bg-neutral-100 dark:hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Updating..." : "Update Status"}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ChangeStatusModal